import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { STATUS_LABELS, STATUS_COLORS, INFRA_TYPES, INVESTMENT_TYPES, PETITION_TYPES, formatMoney, formatDate } from "@/lib/constants";
import { ArrowLeft, Users, Home, Phone, ClipboardList, Banknote, Workflow, Mail, FileDown, Loader2, Package, FileSpreadsheet, Plus, Printer, History } from "lucide-react";
import { YearlySpendChart } from "@/components/YearlySpendChart";
import { toast } from "sonner";
import { VillageAuditTab } from "@/components/VillageAuditTab";
import { APPLICATION_STATUSES } from "@/components/ApplicationFields";

const WL_STATUS = {
  baslangic: "Başlangıç Bilgisi", sonuc: "Sonuç Bilgisi", devam: "Devam Ediyor",
  tamamlandi: "Tamamlandı", planlandi: "Planlandı",
};

const Pill = ({ s }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[s] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
    {STATUS_LABELS[s] || WL_STATUS[s] || s}
  </span>
);

const Stat = ({ icon: Icon, label, value, testId }) => (
  <Card className="p-5 border border-slate-200 shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <div className="font-mono font-semibold text-slate-900 text-lg mt-1.5">{value}</div>
  </Card>
);

function DataTable({ headers, rows, testId, empty }) {
  return (
    <Card className="border border-slate-200 shadow-sm overflow-hidden">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow className="bg-slate-50">{headers.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={headers.length} className="text-center py-10 text-slate-500">{empty}</TableCell></TableRow>
          )}
          {rows.map((cells, i) => (
            <TableRow key={i}>{cells.map((c, j) => (
              <TableCell key={j} className={j === cells.length - 1 ? "min-w-[180px]" : "whitespace-nowrap"}>{c}</TableCell>
            ))}</TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function VillageDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState({});
  const [insts, setInsts] = useState({});
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState("");

  const downloadReport = async (fmt) => {
    setDownloading(fmt);
    try {
      const r = await api.get(`/villages/${id}/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.village?.name || "koy"}_koy_raporu.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(fmt === "pdf" ? "Köy raporu (PDF) indirildi" : "Köy raporu (Excel) indirildi");
    } catch (e) {
      toast.error("Rapor oluşturulamadı");
    } finally {
      setDownloading("");
    }
  };

  useEffect(() => {
    api.get(`/villages/${id}/summary`).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Köy yüklenemedi"));
    api.get("/categories").then((r) => setCats(Object.fromEntries(r.data.map((c) => [c.id, c.name]))));
    api.get("/institutions").then((r) => setInsts(Object.fromEntries(r.data.map((c) => [c.id, c.name]))));
  }, [id]);

  const latestPop = useMemo(() => {
    const p = data?.village?.populations || [];
    return p.length ? [...p].sort((a, b) => b.year - a.year)[0].population : 0;
  }, [data]);

  const back = (
    <Button asChild variant="outline" data-testid="village-detail-back-button">
      <Link to="/koyler"><ArrowLeft className="h-4 w-4 mr-1.5" /> Köyler</Link>
    </Button>
  );

  if (error) {
    return <AppLayout title="Köy Detayı" actions={back}><div className="text-slate-500" data-testid="village-detail-error">{error}</div></AppLayout>;
  }
  if (!data) {
    return <AppLayout title="Köy Detayı" actions={back}><div className="text-slate-500">Yükleniyor...</div></AppLayout>;
  }

  const { village: v, work_logs, investments, infrastructure, petitions, totals, yearly, materials = [] } = data;

  const actions = (
    <>
      <Button onClick={() => downloadReport("pdf")} disabled={!!downloading} className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="village-pdf-download-button">
        {downloading === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
        PDF Rapor
      </Button>
      <Button onClick={() => downloadReport("excel")} disabled={!!downloading} variant="outline"
              data-testid="village-excel-download-button">
        {downloading === "excel" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-700" />}
        Excel Rapor
      </Button>
      <Button asChild variant="outline" data-testid="village-print-button">
        <Link to={`/koyler/${id}/yazdir`} target="_blank"><Printer className="h-4 w-4 mr-1.5 text-slate-700" /> Yazdır</Link>
      </Button>
      {back}
    </>
  );
  const quick = (path) => `${path}?yeni=1&koy=${v.id}&geri=${encodeURIComponent(`/koyler/${v.id}`)}`;

  return (
    <AppLayout title={v.name} subtitle="Köyün tüm çalışma, yatırım, altyapı ve dilekçe geçmişi" actions={actions}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat icon={Users} label="Nüfus" value={latestPop.toLocaleString("tr-TR")} testId="village-detail-population" />
        <Stat icon={Home} label="Hane" value={v.household_count} testId="village-detail-households" />
        <Stat icon={ClipboardList} label="Çalışma Tutarı" value={formatMoney(totals.work_amount)} testId="village-detail-work-total" />
        <Stat icon={Package} label="Malzeme Maliyeti" value={formatMoney(totals.material_total)} testId="village-detail-material-total" />
        <Stat icon={Banknote} label="Yatırım (Gerçekleşen)" value={formatMoney(totals.investment_actual)} testId="village-detail-investment-total" />
      </div>

      <Card className="p-4 border border-emerald-200 bg-emerald-50/50 shadow-sm mb-6 flex flex-wrap items-center gap-3"
            data-testid="village-quick-add">
        <div className="text-sm font-medium text-slate-700 mr-2">Bu köye hızlı kayıt:</div>
        {[
          { to: "/calisma-kayitlari", label: "Yeni Çalışma", icon: ClipboardList, testId: "quick-add-work-log" },
          { to: "/dilekceler", label: "Yeni Dilekçe", icon: Mail, testId: "quick-add-petition" },
          { to: "/yatirimlar", label: "Yeni Yatırım", icon: Banknote, testId: "quick-add-investment" },
          { to: "/altyapi", label: "Yeni Altyapı Projesi", icon: Workflow, testId: "quick-add-infrastructure" },
        ].map((q) => (
          <Button key={q.to} asChild size="sm" variant="outline" className="bg-white" data-testid={q.testId}>
            <Link to={quick(q.to)}><Plus className="h-3.5 w-3.5 mr-1" /><q.icon className="h-3.5 w-3.5 mr-1.5 text-emerald-700" />{q.label}</Link>
          </Button>
        ))}
      </Card>

      <Card className="p-5 border border-slate-200 shadow-sm mb-6" data-testid="village-detail-contacts">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs text-slate-500">Muhtar</div>
            <div className="font-medium text-slate-900 mt-0.5">{v.muhtar_name || "-"}</div>
            {v.muhtar_phone && <div className="flex items-center gap-1 text-xs text-slate-500 mt-1"><Phone className="h-3 w-3" /> {v.muhtar_phone}</div>}
          </div>
          <div>
            <div className="text-xs text-slate-500">Azalar</div>
            <div className="text-slate-700 mt-0.5">
              {v.azalar?.length ? v.azalar.map((a) => `${a.full_name}${a.phone ? ` (${a.phone})` : ""}`).join(", ") : "-"}
            </div>
          </div>
        </div>
        {v.notes && <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-600">{v.notes}</div>}
      </Card>

      <YearlySpendChart data={yearly || []} />

      <Tabs defaultValue="work">
        <div className="max-w-full overflow-x-auto mb-4">
          <TabsList data-testid="village-detail-tabs">
            <TabsTrigger value="work" data-testid="village-tab-work"><ClipboardList className="h-4 w-4 mr-1.5" /> Çalışmalar ({work_logs.length})</TabsTrigger>
            <TabsTrigger value="mat" data-testid="village-tab-materials"><Package className="h-4 w-4 mr-1.5" /> Malzemeler ({materials.length})</TabsTrigger>
            <TabsTrigger value="inv" data-testid="village-tab-investments"><Banknote className="h-4 w-4 mr-1.5" /> Yatırımlar ({investments.length})</TabsTrigger>
            <TabsTrigger value="infra" data-testid="village-tab-infra"><Workflow className="h-4 w-4 mr-1.5" /> Altyapı ({infrastructure.length})</TabsTrigger>
            <TabsTrigger value="pet" data-testid="village-tab-petitions"><Mail className="h-4 w-4 mr-1.5" /> Dilekçeler ({petitions.length})</TabsTrigger>
            <TabsTrigger value="audit" data-testid="village-tab-audit"><History className="h-4 w-4 mr-1.5" /> İşlem Geçmişi</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="work">
          <DataTable testId="village-work-table" empty="Bu köy için çalışma kaydı yok."
            headers={["Tarih", "Yıl", "Kategori", "Miktar", "Tutar", "Durum", "Açıklama"]}
            rows={work_logs.map((w) => [formatDate(w.date), w.year, cats[w.category_id] || "-", `${w.quantity} ${w.unit}`,
              formatMoney(w.amount), <Pill s={w.status} />, w.description || "-"])} />
        </TabsContent>
        <TabsContent value="mat">
          <DataTable testId="village-materials-table" empty="Bu köy için malzeme girişi yok."
            headers={["Tarih", "Malzeme", "Kullanıldığı Yer / Bölge", "Miktar", "Birim Fiyat", "Toplam", "Nasıl Uygulandığı",
              "Uygulayan", "Uygulama Durumu", "Teslim Tarihi", "Kategori", "Açıklama"]}
            rows={materials.map((m) => [formatDate(m.date), m.material_name, m.location || "-", `${m.quantity} ${m.unit}`,
              formatMoney(m.unit_price), formatMoney(m.total_cost), m.application_note || "-", m.applied_by || "-",
              APPLICATION_STATUSES[m.application_status] || "-", m.delivery_date ? formatDate(m.delivery_date) : "-",
              cats[m.category_id] || "-", m.note || "-"])} />
        </TabsContent>
        <TabsContent value="inv">
          <DataTable testId="village-investments-table" empty="Bu köy için yatırım kaydı yok."
            headers={["Başlangıç", "Bitiş", "Başlık", "Yapıldığı Yer / Bölge", "Miktar", "Birim Fiyat", "Nasıl Uygulandığı", "Uygulayan",
              "Uygulama Durumu", "Teslim Tarihi", "Kurum", "Tür", "Bütçe", "Gerçekleşen", "Durum"]}
            rows={investments.map((i) => [i.start_date ? formatDate(i.start_date) : i.year, i.end_date ? formatDate(i.end_date) : "-",
              i.title, i.location || "-", i.quantity ? `${i.quantity} ${i.unit || ""}` : "-", formatMoney(i.unit_price),
              i.application_note || "-", i.applied_by || "-", APPLICATION_STATUSES[i.application_status] || "-",
              i.delivery_date ? formatDate(i.delivery_date) : "-", insts[i.institution_id] || "-", INVESTMENT_TYPES[i.investment_type],
              formatMoney(i.budget), formatMoney(i.actual), <Pill s={i.status} />])} />
        </TabsContent>
        <TabsContent value="infra">
          <DataTable testId="village-infra-table" empty="Bu köy için altyapı projesi yok."
            headers={["Proje", "Tip", "Başlangıç", "Bitiş", "Bütçe", "Gerçekleşen", "Durum"]}
            rows={infrastructure.map((p) => [p.name, INFRA_TYPES[p.type], p.start_date ? formatDate(p.start_date) : "-", p.end_date ? formatDate(p.end_date) : "-",
              formatMoney(p.budget), formatMoney(p.actual_cost), <Pill s={p.status} />])} />
        </TabsContent>
        <TabsContent value="pet">
          <DataTable testId="village-petitions-table" empty="Bu köy için dilekçe yok."
            headers={["Tarih", "Dilekçe Sahibi", "Tür", "Konu", "Durum", "Sonuç"]}
            rows={petitions.map((p) => [formatDate(p.date), p.applicant_name, PETITION_TYPES[p.petition_type], p.subject,
              <Pill s={p.status} />, p.result_note || "-"])} />
        </TabsContent>
        <TabsContent value="audit">
          <VillageAuditTab villageId={id} />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
